// Helper de fechas compartido por los parsers de PDF por proveedor. Normaliza a "YYYY-MM-DD" los
// 5 formatos presentes en los documentos reales (texto pdfjs verificado):
//   - "June 28, 2026"   (Month DD, YYYY - ingles)          -> Canva, Anthropic
//   - "01-Jul-2026"     (DD-Mon-YYYY - ingles)             -> SendGrid
//   - "6/25/2026"       (M/D/YYYY - numerico, orden US)    -> Screenfeed
//   - "30 jun 2026"     (DD mmm YYYY - espanol abreviado)  -> Google Workspace
//   - "2 Jul 2026"      (D Mon YYYY - dia sin cero)        -> AccuWeather
//   - "9 de mayo de 2026" (D de MMMM de YYYY - espanol largo) -> Dropbox
// Best-effort: si el string no matchea ningun formato o el mes es desconocido -> null (el parser
// deja la fecha en null y el usuario la completa a mano). Nunca lanza.

// Mapa mes -> numero por prefijo de 3 letras (ingles Y espanol). slice(0,3) del nombre - completo o
// abreviado, en cualquiera de los dos idiomas - cae siempre en una clave unica sin colisiones
// (ene/jan=1, ago/aug=8, sep/set=9, dic/dec=12, etc.).
const MESES = {
  ene: 1,
  jan: 1,
  feb: 2,
  mar: 3,
  abr: 4,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  ago: 8,
  aug: 8,
  sep: 9,
  set: 9,
  oct: 10,
  nov: 11,
  dic: 12,
  dec: 12,
};

// Nombre de mes (EN/ES, completo o abreviado) -> numero, o null. Quita acentos (combining marks
// U+0300-U+036F tras NFD) y baja a minusculas antes de tomar el prefijo.
const mesANumero = (nombre) => {
  if (!nombre) return null;
  const clave = nombre.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().slice(0, 3);
  return MESES[clave] || null;
};

const fmt = (anio, mes, dia) => `${anio}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;

// Clase de caracteres de nombre de mes: letras ASCII + latin-1/latin-extended acentuadas + punto
// (algunos PDFs abrevian "sept." / meses con acento).
const MES_CHARS = "A-Za-z\\u00C0-\\u017F.";

/**
 * Normaliza un string de fecha crudo de cualquiera de los 5 formatos soportados -> "YYYY-MM-DD".
 * @param {string} raw
 * @returns {string|null}
 */
export const parseFecha = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim();

  // 1. Numerico M/D/YYYY (orden US: mes primero) - "6/25/2026"
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const mes = Number(m[1]);
    const dia = Number(m[2]);
    if (mes >= 1 && mes <= 12 && dia >= 1 && dia <= 31) return fmt(m[3], mes, dia);
    return null;
  }

  // 2. "Month DD, YYYY" (ingles) - "June 28, 2026"
  m = s.match(new RegExp(`^([${MES_CHARS}]+)\\s+(\\d{1,2}),\\s*(\\d{4})$`));
  if (m) {
    const mes = mesANumero(m[1]);
    return mes ? fmt(m[3], mes, Number(m[2])) : null;
  }

  // 3. "DD-Mon-YYYY" (ingles) - "01-Jul-2026"
  m = s.match(new RegExp(`^(\\d{1,2})-([${MES_CHARS}]+)-(\\d{4})$`));
  if (m) {
    const mes = mesANumero(m[2]);
    return mes ? fmt(m[3], mes, Number(m[1])) : null;
  }

  // 4. "DD mmm YYYY" / "D Mon YYYY" (espanol o ingles, separado por espacios) - "30 jun 2026" / "2 Jul 2026"
  m = s.match(new RegExp(`^(\\d{1,2})\\s+([${MES_CHARS}]+)\\s+(\\d{4})$`));
  if (m) {
    const mes = mesANumero(m[2]);
    return mes ? fmt(m[3], mes, Number(m[1])) : null;
  }

  // 5. "D de MMMM de YYYY" (espanol largo) - "9 de mayo de 2026" -> Dropbox. mesANumero ya resuelve
  // el mes en espanol completo (mayo->may->5). Va ultimo para no interferir con los formatos previos.
  m = s.match(new RegExp(`^(\\d{1,2}) de ([${MES_CHARS}]+) de (\\d{4})$`, "i"));
  if (m) {
    const mes = mesANumero(m[2]);
    return mes ? fmt(m[3], mes, Number(m[1])) : null;
  }

  return null;
};

/**
 * Deriva el periodo ("YYYY-MM-01") a partir de una fecha "YYYY-MM-DD" ya normalizada: primer dia
 * del mes de la fecha. Regla SISTEMICA -> el periodo NUNCA se extrae del PDF, siempre se deriva de
 * `fecha`. Devuelve null si la fecha es null/invalida.
 * @param {string|null} fecha
 * @returns {string|null}
 */
export const derivarPeriodo = (fecha) => {
  if (!fecha || typeof fecha !== "string" || fecha.length < 7) return null;
  return `${fecha.slice(0, 7)}-01`;
};
